import type { Me, NewOrder, OrderStatus } from "@bioflow/contracts";

/**
 * Состояние формы чекаута, правила проверки и сборка тела заказа. Поля — по
 * экрану 9 ТЗ. Настоящая валидация всё равно в API: форму можно обойти,
 * схему нет.
 */
export type CheckoutValues = {
  name: string;
  phone: string;
  telegram: string;
  city: string;
  address: string;
  promoCode: string;
  comment: string;
};

export const EMPTY_VALUES: CheckoutValues = {
  name: "",
  phone: "",
  telegram: "",
  city: "",
  address: "",
  promoCode: "",
  comment: "",
};

/**
 * Начальные значения: контакты — из последнего заказа, если он был, иначе
 * имя и username из Telegram. Промокод и комментарий не переносятся —
 * они у каждого заказа свои.
 */
export function initialValues(me: Me | undefined): CheckoutValues {
  const last = me?.lastContact;
  const tgName = [me?.firstName, me?.lastName].filter(Boolean).join(" ");
  return {
    ...EMPTY_VALUES,
    name: last?.name ?? tgName,
    phone: last?.phone ?? "",
    telegram: last?.telegram ?? (me?.username ? `@${me.username}` : ""),
    city: last?.city ?? "",
    address: last?.address ?? "",
  };
}

/** Цифры, плюс, пробелы, скобки и дефисы; не меньше 10 цифр. */
const PHONE = /^\+?[\d\s()-]{10,}$/;

export function validate(values: CheckoutValues): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!values.name.trim()) errors.name = "Укажите имя";
  if (!PHONE.test(values.phone.trim()) || values.phone.replace(/\D/g, "").length < 10) {
    errors.phone = "Укажите телефон, например +7 900 000-00-00";
  }
  if (!values.city.trim()) errors.city = "Укажите город";
  if (!values.address.trim()) errors.address = "Укажите адрес или способ доставки";
  return errors;
}

export function toNewOrder(
  values: CheckoutValues,
  lines: Array<{ variantId: string; quantity: number }>,
): NewOrder {
  const filled = (value: string) => value.trim().length > 0;
  return {
    contact: {
      name: values.name.trim(),
      phone: values.phone.trim(),
      ...(filled(values.telegram) ? { telegram: values.telegram.trim() } : {}),
    },
    delivery: {
      city: values.city.trim(),
      address: values.address.trim(),
    },
    items: lines,
    ...(filled(values.promoCode) ? { promoCode: values.promoCode.trim() } : {}),
    ...(filled(values.comment) ? { comment: values.comment.trim() } : {}),
  };
}

/** `contact.phone` от API → `phone` в форме. */
export function fieldFromApiPath(path: string): string {
  const segments = path.split(".");
  return segments[segments.length - 1] ?? path;
}

/** Статусы — подписи из ТЗ (экран 13): Оформлен / Подтверждён / Отправлен / Доставлен. */
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  new: "Оформлен",
  confirmed: "Подтверждён",
  shipped: "Отправлен",
  delivered: "Доставлен",
  cancelled: "Отменён",
};

/** Порядок этапов для шкалы прогресса на экране заказа. */
export const ORDER_STAGES: OrderStatus[] = ["new", "confirmed", "shipped", "delivered"];
